import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Modal } from 'bootstrap'
import { t } from '../../i18n.js'
import { isoToDisplayDateOrDash as fmtDate } from '../utils/dateFormat.js'

const REVIEW_TYPE_VALUES = [
  'PCT_OF_MARKET_RENT',
  'FIXED_PCT_CHANGE',
  'FIXED_AMOUNT_CHANGE',
  'SET_RENT_AMOUNT'
]

const MOVEMENT_VALUES = [ 'UPWARD_ONLY', 'UP_OR_DOWN' ]

const DEFAULT_REVIEW_TYPE = 'PCT_OF_MARKET_RENT'
const DEFAULT_MOVEMENT    = 'UPWARD_ONLY'

const movementApplicable = (type) => type === 'PCT_OF_MARKET_RENT'

// Render a numeric string with thousand separators while preserving the
// user-typed decimal precision (EVA-154).
//   '3628810.8'  → '3,628,810.8'
//   '510.2089'   → '510.2089'
//   '1234.'      → '1,234.'           (mid-typing: trailing dot kept)
//   ''           → ''                  (empty stays empty for placeholder UX)
const formatThousands = (value) => {
  if (value === null || value === undefined || value === '') return ''
  const stripped = String(value).replace(/,/g, '')
  const [intPart, decPart] = stripped.split('.')
  if (intPart === '' || intPart === '-') {
    return decPart === undefined ? intPart : `${intPart}.${decPart}`
  }
  const intNum = Number(intPart)
  if (!Number.isFinite(intNum)) return stripped
  const intFormatted = intNum.toLocaleString('en-US')
  return decPart === undefined ? intFormatted : `${intFormatted}.${decPart}`
}

const stripCommas = (value) => String(value ?? '').replace(/,/g, '')

const localId = () => `rrv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const buildEmptyRow = () => ({
  _localId: localId(),
  id: null,
  review_date: '',
  review_type: DEFAULT_REVIEW_TYPE,
  input_value: '',
  movement_allowed: DEFAULT_MOVEMENT,
  notes: ''
})

const addYearsIso = (iso, years) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso
  const [y, m, d] = iso.split('-').map(Number)
  const target = new Date(Date.UTC(y + years, m - 1, d))
  if (target.getUTCMonth() !== m - 1) target.setUTCDate(0)
  const yyyy = target.getUTCFullYear()
  const mm = String(target.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(target.getUTCDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const generateDateSequence = ({ start, repeatYears, until, valuationDate }) => {
  if (!start || !until || repeatYears <= 0) return []
  let cursor = start
  // Advance past valuation date
  let guard = 0
  while (valuationDate && cursor <= valuationDate && guard < 120) {
    const next = addYearsIso(cursor, repeatYears)
    if (next <= cursor) break
    cursor = next
    guard += 1
  }
  if (cursor > until) return []
  const out = []
  guard = 0
  while (cursor <= until && guard < 120) {
    out.push(cursor)
    const next = addYearsIso(cursor, repeatYears)
    if (next <= cursor) break
    cursor = next
    guard += 1
  }
  return out
}

export default function RentReviewsModal({ csrfToken, translations }) {
  const [open, setOpen] = useState(false)
  const [tenant, setTenant] = useState(null)
  const [reviews, setReviews] = useState([])
  const [valuationDate, setValuationDate] = useState('')
  const [replaceUrl, setReplaceUrl] = useState('')
  const [requestId, setRequestId] = useState('')
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  // Generator panel state
  const [startFrom, setStartFrom] = useState('lease_start')
  const [manualStart, setManualStart] = useState('')
  const [repeatEvery, setRepeatEvery] = useState(5)
  const [generateUntil, setGenerateUntil] = useState('applied_lease_end')
  const [customUntil, setCustomUntil] = useState('')
  const [genMessage, setGenMessage] = useState('')

  const modalRef = useRef(null)
  const bsModalRef = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {}
      setTenant(d.tenant || null)
      const initialReviews = Array.isArray(d.reviews) ? d.reviews : []
      setReviews(initialReviews.map(r => ({
        _localId: localId(),
        id: r.id,
        review_date: r.review_date || '',
        review_type: r.review_type || DEFAULT_REVIEW_TYPE,
        input_value: r.input_value === null || r.input_value === undefined ? '' : String(r.input_value),
        movement_allowed: r.movement_allowed || (movementApplicable(r.review_type) ? DEFAULT_MOVEMENT : ''),
        notes: r.notes || ''
      })))
      setValuationDate(d.valuationDate || '')
      setReplaceUrl(d.replaceUrl || '')
      setRequestId(d.requestId || '')
      setStartFrom('lease_start')
      setManualStart('')
      setRepeatEvery(5)
      setGenerateUntil('applied_lease_end')
      setCustomUntil('')
      setGenMessage('')
      setErrorMsg('')
      setOpen(true)
    }
    window.addEventListener('openRentReviewsModal', handler)
    return () => window.removeEventListener('openRentReviewsModal', handler)
  }, [])

  useEffect(() => {
    if (!modalRef.current) return
    if (open) {
      if (!bsModalRef.current) bsModalRef.current = new Modal(modalRef.current)
      bsModalRef.current.show()
      const onHidden = () => {
        setOpen(false)
        setTenant(null)
      }
      modalRef.current.addEventListener('hidden.bs.modal', onHidden, { once: true })
    } else if (bsModalRef.current) {
      bsModalRef.current.hide()
    }
  }, [open])

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  const updateRow = useCallback((localId, patch) => {
    setReviews(prev => prev.map(r => (r._localId === localId ? { ...r, ...patch } : r)))
  }, [])

  const changeReviewType = useCallback((localId, type) => {
    setReviews(prev => prev.map(r => {
      if (r._localId !== localId) return r
      const movement = movementApplicable(type) ? (r.movement_allowed || DEFAULT_MOVEMENT) : ''
      return { ...r, review_type: type, movement_allowed: movement }
    }))
  }, [])

  const addRow = useCallback(() => {
    setReviews(prev => [...prev, buildEmptyRow()])
  }, [])

  const deleteRow = useCallback((localId) => {
    setReviews(prev => prev.filter(r => r._localId !== localId))
  }, [])

  const fillDown = useCallback(() => {
    setReviews(prev => {
      const [first, ...rest] = prev
      if (!first || first.input_value === '') return prev
      return [first, ...rest.map(r => ({
        ...r,
        review_type: first.review_type,
        input_value: first.input_value,
        movement_allowed: movementApplicable(first.review_type) ? (first.movement_allowed || DEFAULT_MOVEMENT) : ''
      }))]
    })
  }, [])

  const resolveUntilDate = useCallback(() => {
    if (!tenant) return ''
    if (generateUntil === 'applied_lease_end') return tenant.applied_lease_end_date || tenant.lease_end_date || ''
    if (generateUntil === 'lease_end') return tenant.lease_end_date || ''
    return customUntil
  }, [tenant, generateUntil, customUntil])

  // Per EVA-152: when source = Lease Start, the *first* review date should
  // be the first anniversary (Lease Start + repeatEvery years), not the lease
  // commencement itself — there's no review on day 0 of the lease.
  // Manual mode keeps the user-typed date verbatim.
  const firstReviewDate = useMemo(() => {
    if (!tenant) return ''
    if (startFrom === 'manual') return manualStart

    const anchor = tenant.lease_start_date || ''
    const years = Number(repeatEvery)
    if (!anchor || !Number.isFinite(years) || years <= 0) return anchor
    return addYearsIso(anchor, years)
  }, [tenant, startFrom, manualStart, repeatEvery])

  const generate = useCallback(() => {
    if (!firstReviewDate) {
      setGenMessage(t('generator.messages.start_missing', translations))
      return
    }
    if (!Number.isFinite(Number(repeatEvery)) || Number(repeatEvery) <= 0) {
      setGenMessage(t('generator.messages.repeat_invalid', translations))
      return
    }
    const until = resolveUntilDate()
    if (!until) {
      setGenMessage(t('generator.messages.until_missing', translations))
      return
    }
    const dates = generateDateSequence({
      start: firstReviewDate,
      repeatYears: Number(repeatEvery),
      until,
      valuationDate
    })
    if (dates.length === 0) {
      setGenMessage(t('generator.messages.no_dates', translations))
      return
    }
    setReviews(prev => {
      const existing = new Set(prev.map(r => r.review_date).filter(Boolean))
      const additions = dates
        .filter(d => !existing.has(d))
        .map(d => ({ ...buildEmptyRow(), review_date: d }))
      setGenMessage(
        additions.length === 0
          ? t('generator.messages.all_present', translations)
          : t('generator.messages.generated', translations, { count: additions.length })
      )
      return [...prev, ...additions].sort((a, b) => (a.review_date || '').localeCompare(b.review_date || ''))
    })
  }, [firstReviewDate, repeatEvery, resolveUntilDate, valuationDate, translations])

  const blockedPastDates = useMemo(
    () => reviews.some(r => r.review_date && valuationDate && r.review_date <= valuationDate),
    [reviews, valuationDate]
  )

  const save = useCallback(async () => {
    if (!replaceUrl) return
    setSaving(true)
    setErrorMsg('')
    try {
      const payload = {
        request_id: requestId,
        rows: reviews.map(r => ({
          id: r.id,
          review_date: r.review_date || null,
          review_type: r.review_type,
          input_value: r.input_value === '' ? null : Number(r.input_value),
          movement_allowed: movementApplicable(r.review_type) ? (r.movement_allowed || DEFAULT_MOVEMENT) : '',
          notes: r.notes || ''
        }))
      }
      const response = await fetch(replaceUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify(payload)
      })
      const result = await response.json()
      if (result.success) {
        close()
      } else {
        const errors = result.errors?.errors_by_index
        if (errors && typeof errors === 'object') {
          const firstIdx = Object.keys(errors)[0]
          setErrorMsg(t('row_error', translations, { n: Number(firstIdx) + 1, detail: JSON.stringify(errors[firstIdx]) }))
        } else {
          setErrorMsg(result.message || result.error || t('save_failed', translations))
        }
      }
    } catch (err) {
      console.error('Save rent reviews failed', err)
      setErrorMsg(t('save_failed', translations))
    } finally {
      setSaving(false)
    }
  }, [replaceUrl, requestId, reviews, csrfToken, close, translations])


  const fmtMoney = (v) => {
    if (v === null || v === undefined || v === '') return '—'
    const n = Number(v)
    if (!Number.isFinite(n)) return '—'
    return new Intl.NumberFormat('en-GB').format(n)
  }

  return (
    <div
      ref={modalRef}
      className="modal fade"
      tabIndex={-1}
      aria-labelledby="rentReviewsModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="rentReviewsModalLabel">
              {t('title', translations, { tenant: tenant?.title || t('unnamed_tenant', translations) })}
              <span className="ms-2 small text-muted">
                {t('review_count', translations, { count: reviews.length })}
              </span>
            </h5>
            <button type="button" className="btn-close" onClick={close} aria-label={t('close', translations)} />
          </div>

          <div className="modal-body">
            {errorMsg && <div className="alert alert-danger py-2 mb-3 small">{errorMsg}</div>}

            <div className="row g-2 mb-3 small text-muted">
              <div className="col"><div>{t('tenant_info.contracted_rent_pa', translations)}</div><strong className="text-body">{fmtMoney(tenant?.annual_rent)}</strong></div>
              <div className="col"><div>{t('tenant_info.lease_start', translations)}</div><strong className="text-body">{fmtDate(tenant?.lease_start_date)}</strong></div>
              <div className="col"><div>{t('tenant_info.applied_lease_end', translations)}</div><strong className="text-body">{fmtDate(tenant?.applied_lease_end_date)}</strong></div>
              <div className="col"><div>{t('tenant_info.payment_schedule', translations)}</div><strong className="text-body">{tenant?.payment_schedule || '—'}</strong></div>
              <div className="col"><div>{t('tenant_info.area', translations)}</div><strong className="text-body">{fmtMoney(tenant?.area)}</strong></div>
            </div>

            <div className="row g-3">
              <div className="col-lg-8">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>{t('inputs.title', translations)}</strong>
                  <div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={fillDown}
                      disabled={reviews.length < 2 || !reviews[0]?.input_value}
                    >
                      {t('inputs.fill_down', translations)}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addRow}>
                      {t('inputs.add_row', translations)}
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '140px' }}>{t('inputs.columns.review_date', translations)}</th>
                        <th style={{ width: '170px' }}>{t('inputs.columns.review_type', translations)}</th>
                        <th style={{ width: '120px' }}>{t('inputs.columns.value', translations)}</th>
                        <th style={{ width: '140px' }}>{t('inputs.columns.movement', translations)}</th>
                        <th style={{ width: '40px' }} aria-label={t('inputs.columns.delete', translations)} />
                      </tr>
                    </thead>
                    <tbody>
                      {reviews.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-muted text-center py-3">
                            {t('inputs.empty_state', translations)}
                          </td>
                        </tr>
                      ) : (
                        reviews.map(r => {
                          const movementVisible = movementApplicable(r.review_type)
                          const isPast = r.review_date && valuationDate && r.review_date <= valuationDate
                          return (
                            <tr key={r._localId} className={isPast ? 'table-warning' : undefined}>
                              <td>
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  value={r.review_date}
                                  onChange={e => updateRow(r._localId, { review_date: e.target.value })}
                                />
                              </td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={r.review_type}
                                  onChange={e => changeReviewType(r._localId, e.target.value)}
                                >
                                  {REVIEW_TYPE_VALUES.map(value => (
                                    <option key={value} value={value}>{t(`types.${value}`, translations)}</option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control form-control-sm text-end"
                                  value={formatThousands(r.input_value)}
                                  onChange={e => updateRow(r._localId, { input_value: stripCommas(e.target.value) })}
                                />
                              </td>
                              <td>
                                {movementVisible ? (
                                  <select
                                    className="form-select form-select-sm"
                                    value={r.movement_allowed || DEFAULT_MOVEMENT}
                                    onChange={e => updateRow(r._localId, { movement_allowed: e.target.value })}
                                  >
                                    {MOVEMENT_VALUES.map(value => (
                                      <option key={value} value={value}>{t(`movement.${value}`, translations)}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span className="text-muted small">{t('inputs.movement_na', translations)}</span>
                                )}
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm text-danger p-0"
                                  onClick={() => deleteRow(r._localId)}
                                  aria-label={t('inputs.delete_row', translations)}
                                  title={t('inputs.delete_row', translations)}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="p-3 border rounded bg-light">
                  <strong>{t('generator.title', translations)}</strong>
                  <div className="mt-2 mb-2">
                    <label className="form-label small mb-1">{t('generator.start_from', translations)}</label>
                    <select
                      className="form-select form-select-sm"
                      value={startFrom}
                      onChange={e => setStartFrom(e.target.value)}
                    >
                      <option value="lease_start">{t('generator.start_options.lease_start', translations)}</option>
                      <option value="manual">{t('generator.start_options.manual', translations)}</option>
                    </select>
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">{t('generator.first_review_date', translations)}</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={firstReviewDate}
                      readOnly={startFrom === 'lease_start'}
                      onChange={e => setManualStart(e.target.value)}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">{t('generator.repeat_every', translations)}</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control form-control-sm"
                      value={repeatEvery}
                      onChange={e => setRepeatEvery(e.target.value)}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">{t('generator.generate_until', translations)}</label>
                    <select
                      className="form-select form-select-sm"
                      value={generateUntil}
                      onChange={e => setGenerateUntil(e.target.value)}
                    >
                      <option value="applied_lease_end">{t('generator.until_options.applied_lease_end', translations)}</option>
                      <option value="lease_end">{t('generator.until_options.lease_end', translations)}</option>
                      <option value="custom">{t('generator.until_options.custom', translations)}</option>
                    </select>
                  </div>
                  {generateUntil === 'custom' && (
                    <div className="mb-2">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={customUntil}
                        onChange={e => setCustomUntil(e.target.value)}
                      />
                    </div>
                  )}
                  {genMessage && <div className="small text-muted mb-2">{genMessage}</div>}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary w-100"
                    onClick={generate}
                  >
                    {t('generator.generate_button', translations)}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <span className="me-auto small text-muted">
              {blockedPastDates
                ? t('footer.blocked_dates', translations, { date: fmtDate(valuationDate) })
                : t('footer.info', translations)}
            </span>
            <button type="button" className="btn btn-secondary" onClick={close} disabled={saving}>
              {t('cancel', translations)}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={save}
              disabled={saving || blockedPastDates}
            >
              {saving ? t('saving', translations) : t('save', translations)}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
